import { useQueries } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Copy, Plus, RefreshCcw, Wallet } from "lucide-react";

import { viewWalletAccount, viewWalletBalance } from "src/api/wallet";
import { useAuth } from "src/providers/AuthProvider";
import useHasPermissions from "src/hooks/useHasPermissions";
import CustomModal from "src/components/CustomModal";
import TopUpWallet from "src/components/modals/TopUpWallet";
import PermissionWrapper from "./PermissionWrapper";
import { Button } from "src/components/ui/button";
import { Skeleton } from "src/components/ui/skeleton";
import { copyToClipboard, renderCurrency, showInfoToast, showSuccessToast } from "src/lib/utils";

const DetailRow = ({ label, value, action }: { label: string; value?: string; action?: React.ReactNode }) => (
	<div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-1.5">
		<span className="tw-text-[13px] tw-text-text-soft">{label}</span>
		<span className="tw-flex tw-items-center tw-gap-2 tw-text-[13.5px] tw-font-semibold tw-text-text">
			{value || "N/A"}
			{action}
		</span>
	</div>
);

/** Wallet hero card + linked account details (prototype `#wallet`). */
const WalletDetails = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const canViewWalletBalance = useHasPermissions("view_wallet_balance");
	const canViewWalletAccount = useHasPermissions("view_wallet_account");

	const [{ data: rawBalance, refetch, isLoading: isBalanceLoading }, { data: rawAccount, isLoading: isAccountLoading }] = useQueries({
		queries: [
			{ queryKey: ["wallet-balance"], queryFn: viewWalletBalance, enabled: canViewWalletBalance },
			{ queryKey: ["wallet-account"], queryFn: viewWalletAccount, enabled: canViewWalletAccount },
		],
	});

	const walletBalance = rawBalance?.data;
	const walletAccount = rawAccount?.data;

	const copyAccount = async (accountNumber: string) => {
		await copyToClipboard(accountNumber);
		showSuccessToast("Account number copied successfully");
	};

	const requireBvn = () => {
		if (!user?.bvn_verified) {
			showInfoToast("Please verify your BVN before proceeding");
			navigate("/account");
		}
	};

	return (
		<div className="tw-grid tw-gap-4 lg:tw-grid-cols-2">
			<PermissionWrapper permission="view_wallet_balance">
				<div className="tw-rounded-lg tw-bg-gradient-to-br tw-from-navy tw-to-navy-deep tw-p-5 tw-text-white tw-shadow-card">
					<div className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-medium tw-text-white/70">
						<Wallet className="tw-h-4 tw-w-4" />
						Wallet Balance
					</div>
					{isBalanceLoading ? (
						<Skeleton className="tw-h-9 tw-w-48 tw-bg-white/20" />
					) : (
						<p className="tw-break-words tw-text-[28px] tw-font-bold sm:tw-text-[32px]">{renderCurrency(walletBalance ?? 0)}</p>
					)}
					<div className="tw-mt-5 tw-flex tw-flex-wrap tw-gap-2">
						<CustomModal
							title="Top Up Wallet"
							trigger={
								<Button size="sm" className="tw-gap-1.5 tw-bg-teal tw-text-white hover:tw-bg-teal-dark" onClick={requireBvn}>
									<Plus className="tw-h-4 tw-w-4" />
									Top Up
								</Button>
							}
						>
							<TopUpWallet />
						</CustomModal>
						<Button
							size="sm"
							variant="outline"
							className="tw-gap-1.5 tw-border-white/25 !tw-bg-white/10 !tw-text-white hover:!tw-bg-white/20"
							onClick={() => refetch()}
						>
							<RefreshCcw className="tw-h-4 tw-w-4" />
							Refresh
						</Button>
					</div>
				</div>
			</PermissionWrapper>

			<PermissionWrapper permission="view_wallet_account">
				<div className="tw-rounded-lg tw-border tw-border-border tw-bg-white tw-p-5 tw-shadow-card">
					<p className="tw-mb-3 tw-text-[15px] tw-font-bold tw-text-text">Account Details</p>
					{isAccountLoading ? (
						<div className="tw-space-y-2">
							<Skeleton className="tw-h-5 tw-w-full" />
							<Skeleton className="tw-h-5 tw-w-2/3" />
						</div>
					) : (
						<>
							<DetailRow
								label="Account number"
								value={walletAccount?.account_number}
								action={
									walletAccount?.account_number ? (
										<button
											type="button"
											aria-label="Copy account number"
											onClick={() => copyAccount(walletAccount.account_number as string)}
											className="tw-rounded-lg tw-p-1.5 tw-text-text-soft tw-transition-colors hover:tw-bg-bg hover:tw-text-text"
										>
											<Copy className="tw-h-3.5 tw-w-3.5" />
										</button>
									) : null
								}
							/>
							<DetailRow label="Account name" value={walletAccount?.account_name} />
							<DetailRow label="Bank name" value={walletAccount?.bank_name} />
						</>
					)}
				</div>
			</PermissionWrapper>
		</div>
	);
};

export default WalletDetails;
